import { AssistantItem } from '@repo/shared/dtos/assistant'
import { isEmptyString } from '@repo/shared/utils'
import { Button } from '@repo/ui/button'
import { useScrollToEnd, useT } from '@repo/ui/hooks'
import { ScrollArea } from '@repo/ui/scrollArea'
import { Textarea } from '@repo/ui/textarea'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { cn, onInput } from '@repo/ui/utils'
import { debounce } from 'lodash'
import { ContactRoundIcon, ListTodoIcon, Loader2, RefreshCwIcon } from 'lucide-react'
import { RefObject, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { IoSparklesSharp } from 'react-icons/io5'
import { useLocation } from 'react-router-dom'

import { FeedSection } from '@/components/assistant/feed/FeedSection'
import { AssistantMessage } from '@/components/assistant/messages/AssistantMessage'
import { AssistantTask, TaskLoader } from '@/components/assistant/messages/AssistantTask'
import { MessageHandler } from '@/components/assistant/messages/MessageHandler'
import { GenerateMoreTasksButton } from '@/components/assistant/messages/TasksHandler'
import {
  CollapsibleCard,
  CollapsibleCardContent,
  CollapsibleCardFooter,
  CollapsibleCardHeader,
  CollapsibleCardLeftButtons,
  CollapsibleCardTitle,
  ItemUpdatedDate,
} from '@/components/CollapsibleCard'
import { useDelayedLoader, useTrace } from '@/hooks'
import { isRoute, RouteNames } from '@/routes'
import { storeActions } from '@/store/actions'
import { Beneficiary } from '@/store/beneficiary'
import { useStoreDispatch, useStoreState } from '@/store/hooks'
import { Task } from '@/store/task'

import { AssistantHeaderLight } from './AssistantHeader'
import { RadarLoader } from './RadarLoader'

export function Assistant({ beneficiary }: { beneficiary?: Beneficiary }) {
  const location = useLocation()
  const pathname = useMemo(() => location.pathname, [location.pathname])
  const isThinking = useStoreState((state) => state.assistant.isThinking)
  const isGeneratingProfile = useStoreState(
    (state) => state.assistant.isGeneratingBeneficiaryProfile,
  )
  const isGeneratingTask = useStoreState((state) => state.assistant.isGeneratingTask)
  const isGeneratingMessage = useStoreState((state) => state.assistant.isGeneratingMessage)
  const loadingTransKeys = useStoreState((state) => state.assistant.loadingTransKeys)

  const [mode, setMode] = useState<'profile' | 'tasks' | 'chat'>('profile')

  const feed = useStoreState((state) => state.assistant.feed)

  const generatedTasks = feed.filter((item) => item.type === 'tasks')[0]?.tasks || []

  const messages = feed.filter((item) => item.type === 'message')
  const hasMessages = messages.length > 0

  useEffect(() => {
    if (isGeneratingMessage || hasMessages) {
      setMode('chat')
    } else if (isRoute(RouteNames.BeneficiaryTasks, pathname)) {
      setMode('tasks')
    } else if (isRoute(RouteNames.Beneficiaries, pathname)) {
      setMode('profile')
    }
  }, [pathname, isGeneratingMessage, hasMessages])

  const assistantBottomRef = useRef<HTMLDivElement>(null)
  const scrollToFeedEnd = useScrollToEnd(assistantBottomRef, 'smooth')
  useEffect(() => {
    // Delay slightly to allow DOM update before scrolling
    const timer = setTimeout(() => scrollToFeedEnd(), 100)
    return () => clearTimeout(timer)
  }, [feed.length, scrollToFeedEnd])
  return (
    <div className="flex h-full min-w-full items-end overflow-hidden px-0 py-4 lg:py-3 2xl:px-4">
      <div className="relative flex h-full max-h-full min-w-full items-center justify-center overflow-hidden">
        <div className="relative flex h-full max-h-full min-w-full flex-col overflow-hidden rounded-xl">
          {isThinking && <RadarLoader />}

          <AssistantHeaderLight isThinking={isThinking} loadingTransKeys={loadingTransKeys} />

          <ScrollArea className="h-full">
            <div className="mb-4 space-y-4 px-4">
              {beneficiary ? (
                <>
                  <ProfileSummary
                    beneficiary={beneficiary}
                    isLoading={isGeneratingProfile}
                    open={mode === 'profile'}
                    onOpenChange={(open) => setMode(() => (open ? 'profile' : 'chat'))}
                  />
                  <TasksHandler
                    isLoading={isGeneratingTask}
                    open={mode === 'tasks'}
                    generatedTasks={generatedTasks}
                    onOpenChange={(open) => setMode(() => (open ? 'tasks' : 'chat'))}
                  />
                  <Chat isLoading={isGeneratingMessage} messages={messages} />{' '}
                </>
              ) : (
                <FeedSection isThinking={isThinking} />
              )}
            </div>
            <div className="h-2" id="assistant-end" ref={assistantBottomRef} />
          </ScrollArea>

          {beneficiary && (
            <AssistantInput beneficiary={beneficiary} isDisabled={isGeneratingMessage} />
          )}
        </div>
      </div>
    </div>
  )
}

const MAX_HEIGHT = 360

function AssistantInput({
  isDisabled,
  beneficiary,
}: {
  isDisabled: boolean
  beneficiary: Beneficiary
}) {
  const dispatch = useStoreDispatch()
  const { t } = useT({ keyPrefix: 'assistant.ask' })
  const generationId = useStoreState((state) => state.assistant.generationId)

  const [input, setInput] = useState<string>('')
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)
  useAutosizeTextArea(textAreaRef, input, MAX_HEIGHT)

  const isEmpty = isEmptyString(input)

  const handleSubmit = () => {
    if (!generationId || isEmpty || isDisabled) return

    void dispatch(
      storeActions.assistant.askFor.beneficiary.information({
        generationId,
        input,
      }),
    )
    setInput('')
  }

  useEffect(() => {
    if (isDisabled) return
    textAreaRef.current?.focus()
  }, [isDisabled])

  return (
    <div className="mx-4 flex flex-col gap-2">
      <Textarea
        ref={textAreaRef}
        className="placeholder:text-primary-200 text-primary-100 shadow-primary/80 bg-primary-950 select-none resize-none overflow-visible rounded-lg border-transparent bg-opacity-90 p-4 text-base shadow-md placeholder:select-none"
        placeholder={t('placeholder', { name: beneficiary.fullName })}
        value={input}
        onChange={onInput(setInput)}
        onKeyDown={(e) => {
          if (e.metaKey && e.key === 'Enter') {
            handleSubmit()
          }
        }}
        style={{ maxHeight: `${MAX_HEIGHT}px` }}
        disabled={isDisabled}
        autoFocus
      />

      <div className="flex justify-end">
        <Button
          type="submit"
          variant="highlight"
          className="shadow-primary-600/50 w-fit shadow-md"
          onClick={handleSubmit}
          disabled={isDisabled || isEmpty}
        >
          <IoSparklesSharp className="size-4" />
          {t('cta')}
        </Button>
      </div>
    </div>
  )
}

const useAutosizeTextArea = (
  textAreaRef: RefObject<HTMLTextAreaElement>,
  value: string,
  maxHeight?: number,
) => {
  useLayoutEffect(() => {
    const textArea = textAreaRef.current
    if (textArea) {
      // Temporarily shrink the textarea to calculate the new scroll height
      textArea.style.height = 'auto'
      const scrollHeight = textArea.scrollHeight

      // If a maxHeight is set and the scrollHeight exceeds it, cap the height and enable scrolling
      if (maxHeight && scrollHeight > maxHeight) {
        textArea.style.height = `${maxHeight}px`
        textArea.style.overflowY = 'auto'
      } else {
        // Otherwise, set the height to the scroll height and hide the scrollbar
        textArea.style.height = `${scrollHeight}px`
        textArea.style.overflowY = 'hidden'
      }
    }
  }, [textAreaRef, value, maxHeight])
}
function TasksHandler({
  isLoading,
  open,
  onOpenChange,
  generatedTasks,
}: {
  isLoading: boolean
  generatedTasks: Task[]
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const dispatch = useStoreDispatch()
  const { t } = useT({ keyPrefix: 'assistant.tasks' })
  const generationId = useStoreState((state) => state.assistant.generationId)

  const debouncedTaskDispatcher = useRef(
    debounce((generationId: string, generatedTasks: Task[]) => {
      void dispatch(
        storeActions.assistant.generate.beneficiary.caseWorkerTasks.start({
          generationId,
          previouslyGeneratedTasks: generatedTasks,
        }),
      )
    }, 200),
  ).current

  useEffect(() => {
    if (!generationId) return
    if (generatedTasks.length > 0) return
    if (!open) return
    debouncedTaskDispatcher(generationId, generatedTasks) //FIXME clean this part, shouldn't be in a useEffect?
  }, [open, generationId, debouncedTaskDispatcher])
  return (
    <AssistantMessage>
      <CollapsibleCard variant="transparent" open={open} onOpenChange={onOpenChange}>
        <CollapsibleCardHeader>
          <CollapsibleCardLeftButtons>
            <div className="p-4">
              {isLoading ? (
                <Loader2 className="size-6 animate-spin" />
              ) : (
                <ListTodoIcon className="size-6" />
              )}
            </div>
          </CollapsibleCardLeftButtons>
          <CollapsibleCardTitle>{t('title')}</CollapsibleCardTitle>
        </CollapsibleCardHeader>

        <CollapsibleCardContent>
          <div className="flex flex-col gap-4">
            {generatedTasks.map((task) => (
              <AssistantTask key={task.id} task={task} />
            ))}
            {isLoading && <TaskLoader />}
            {generationId && (
              <GenerateMoreTasksButton
                generationId={generationId}
                isDisabled={isLoading}
                previouslyGeneratedTasks={generatedTasks}
              />
            )}
          </div>
        </CollapsibleCardContent>
      </CollapsibleCard>
    </AssistantMessage>
  )
}

function Chat({ isLoading, messages }: { isLoading: boolean; messages: AssistantItem[] }) {
  if (messages.length === 0 && !isLoading) return null
  return (
    <div className="bg-primary-400/30 shadow-primary-400/50 flex flex-col gap-4 rounded-lg px-4 pb-6 pt-12 shadow-inner">
      {messages.map((message) => (
        <MessageHandler key={message.id} item={message} />
      ))}
      {isLoading && (
        <AssistantMessage className="w-fit">
          <div className="animate-pulse">...</div>
        </AssistantMessage>
      )}
    </div>
  )
}

function ProfileSummary({
  beneficiary,
  isLoading,
  open,
  onOpenChange,
}: {
  beneficiary: Beneficiary
  isLoading: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const dispatch = useStoreDispatch()
  const { t, tc } = useT({ keyPrefix: 'beneficiary.profile' })

  const isLoadingDelayed = useDelayedLoader(isLoading)

  const { Trace } = useTrace({ traceUrl: beneficiary.profile.traceUrl })

  const handleForceRefresh = () => {
    if (!beneficiary) return
    void dispatch(
      storeActions.beneficiary.refreshProfile({
        beneficiaryId: beneficiary.id,
        cacheOptions: { forceReset: true },
      }),
    )
  }
  return (
    <AssistantMessage>
      <CollapsibleCard variant="transparent" open={open} onOpenChange={onOpenChange}>
        <CollapsibleCardHeader>
          <CollapsibleCardLeftButtons>
            <div className="p-4">
              {isLoadingDelayed ? (
                <Loader2 className="size-6 animate-spin" />
              ) : (
                <ContactRoundIcon className="size-6" />
              )}
            </div>
          </CollapsibleCardLeftButtons>
          <CollapsibleCardTitle>{t('summary')}</CollapsibleCardTitle>
        </CollapsibleCardHeader>

        <CollapsibleCardContent>
          <p className="whitespace-pre-wrap text-pretty">{beneficiary.profile.value}</p>

          <CollapsibleCardFooter className="justify-between">
            <div className="flex items-center gap-1.5">
              <ItemUpdatedDate
                labelClassName="hidden"
                className="text-primary-300 text-xs"
                updatedAt={beneficiary.updatedAt}
              />
              <TooltipWrapper content={tc('refresh', { cfl: true })} disabled={isLoadingDelayed}>
                <div
                  onClick={handleForceRefresh}
                  className={cn(!isLoadingDelayed && 'cursor-pointer')}
                >
                  <RefreshCwIcon
                    className={cn(
                      'text-primary-300 hover:text-primary-100 size-3',
                      isLoadingDelayed && 'animate-spin',
                    )}
                  />
                </div>
              </TooltipWrapper>
            </div>
            <Trace />
          </CollapsibleCardFooter>
        </CollapsibleCardContent>
      </CollapsibleCard>
    </AssistantMessage>
  )
}
